import { cn } from '@/lib/utils'
import { ShoppingCart, Skull } from 'lucide-react'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import EncounterWishlistTooltip from './EncounterWishlistTooltip'
import WishLegacyPriority from 'app/frontend/types/custom/WishLegacyPriority'
import WishLegacyPriorityDot from 'app/frontend/base/components/WishLegacyPriorityDot'
import { EventActivityParticipantEntry } from './eventActivityParticipantEntry'
import { resolveEventParticipantCharacter } from './eventActivityParticipants'
import { useTeamEventParticipantsContext } from '../context/TeamEventParticipantsContext'

type Props = {
  entry: EventActivityParticipantEntry
  activityId: number
  className?: string
  contentOnly?: boolean
}

const EventActivityParticipantActivityPlanningInfo = ({
  entry,
  activityId,
  className,
  contentOnly = false,
}: Props) => {
  const { planningInfoByActivityAndCharacter } =
    useTeamEventParticipantsContext()
  const displayCharacter = resolveEventParticipantCharacter(entry, activityId)
  const planningInfo = planningInfoByActivityAndCharacter.get(
    `${activityId}:${displayCharacter.id}`,
  )
  const planningKillCount = planningInfo?.killCount ?? 0
  const planningWishlistUpgrade = planningInfo?.wishlistUpgrade ?? 0
  const planningWishlistEdited = planningInfo?.edited ?? false
  const planningWishlistItems = planningInfo?.wishlistItems ?? []
  const allPlanningWishesManual =
    planningWishlistItems.length > 0 &&
    planningWishlistItems.every(
      (item) =>
        (item.score?.manual?.length ?? 0) > 0 ||
        item.score?.percentage === null,
    )
  const manualUpgradeLevels = Array.from(
    new Set(planningWishlistItems.flatMap((item) => item.score?.manual ?? [])),
  ) as WishLegacyPriority[]

  const killsContent = (
    <span className='flex items-center gap-1'>
      <Tooltip delayDuration={400}>
        <TooltipTrigger>
          <Skull size={13} className='opacity-70' />
        </TooltipTrigger>
        <TooltipContent>
          {planningKillCount} total kill{planningKillCount === 1 ? '' : 's'}
        </TooltipContent>
      </Tooltip>
      <span
        className={
          planningKillCount > 0
            ? 'text-success-foreground'
            : 'text-muted-foreground'
        }
      >
        {planningKillCount}
      </span>
    </span>
  )
  const wishlistContent =
    planningWishlistUpgrade > 0 || allPlanningWishesManual || contentOnly ? (
      <EncounterWishlistTooltip items={planningWishlistItems}>
        <span className='flex items-center gap-1'>
          <ShoppingCart size={13} className='opacity-70' />
          <span
            className={cn(
              planningWishlistEdited
                ? 'text-warning-foreground'
                : planningWishlistUpgrade > 0
                ? 'text-success-foreground'
                : 'text-foreground',
            )}
          >
            {allPlanningWishesManual ? (
              <span className='flex items-center gap-1'>
                {manualUpgradeLevels.map((level) => (
                  <WishLegacyPriorityDot key={level} priority={level} />
                ))}
              </span>
            ) : (
              <>
                {planningWishlistUpgrade.toLocaleString('en-US', {
                  maximumFractionDigits: 1,
                  minimumFractionDigits: 0,
                })}
                %
              </>
            )}
          </span>
        </span>
      </EncounterWishlistTooltip>
    ) : (
      <span className='opacity-0'>-</span>
    )

  const content = (
    <div className='flex items-center justify-between gap-2'>
      {killsContent}
      {planningWishlistUpgrade > 0 || allPlanningWishesManual || contentOnly
        ? wishlistContent
        : null}
    </div>
  )

  if (contentOnly) return content

  return (
    <div
      className={cn(
        'rounded-b-md border border-border bg-card-nested px-1.5 pt-px pb-0.5 text-xs',
        className,
      )}
    >
      {content}
    </div>
  )
}

export default EventActivityParticipantActivityPlanningInfo
